import { useState, useCallback } from 'react';
import jxLogger from '../../../services/utils/jxLogger.js';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import { AnchorCalculator } from '@jxUml';
import ShapeHitTester from './ShapeHitTester.js';
import outputMsgEngine from '../../../Widgets/OutputMsgWidget/Core/OutputMsgEngine.js';
import { DEBUG_LOGGING_ENABLED } from '../../../services/utils/debugMode.js';

const { logDebug } = jxLogger('UmlCanvas');

/**
 * Connector anchor drag interactions. Ctrl+drag on an anchor handle reattaches
 * the connector end to another shape (draft line, hit-test on drop); a plain
 * drag repositions the anchor along the current shape's border (live, per mousemove).
 * @param {Function} toWorldPoint - screen -> world coordinate conversion
 * @param {Function} getShapeById - shape lookup by id
 * @returns {{reattachDraft: object|null, reattachStart: {x:number,y:number}|null, handleAnchorDragStart: Function}}
 */
export default function useAnchorReattach(toWorldPoint, getShapeById) {
    const [reattachDraft, setReattachDraft] = useState(null); // { connectorId, variant, fixedShapeId, x, y } in world coords | null

    const handleAnchorDragStart = useCallback(
        function startAnchorDrag(connector, variant, e) {
            const isReattachMode = e.ctrlKey || e.metaKey;
            const fixedShapeId = variant === 'start' ? connector.toShapeId : connector.fromShapeId;
            const movingShapeId = variant === 'start' ? connector.fromShapeId : connector.toShapeId;
            const startWorld = toWorldPoint(e.clientX, e.clientY);
            logDebug(
                'handleAnchorDragStart',
                `connectorId=${connector.id} variant=${variant} isReattachMode=${isReattachMode} fixedShapeId=${fixedShapeId} movingShapeId=${movingShapeId}`,
            );

            const movingShape = getShapeById(movingShapeId);

            if (DEBUG_LOGGING_ENABLED) {
                outputMsgEngine.addMsg(
                    'Debug',
                    'log',
                    `${connector.id.slice(0, 8)} | Anchor (${variant}) | mousedown | x:${Math.round(startWorld.x)} y:${Math.round(startWorld.y)}`,
                );
            }

            if (isReattachMode) {
                setReattachDraft({ connectorId: connector.id, variant, fixedShapeId, ...startWorld });
                document.body.classList.add('uml-reattach-dragging');
            }
            document.body.style.cursor = 'crosshair';

            function onMouseMove(ev) {
                ev.stopImmediatePropagation();
                const worldPoint = toWorldPoint(ev.clientX, ev.clientY);
                if (isReattachMode) {
                    setReattachDraft(function updateDraftPos(prev) {
                        return prev ? { ...prev, ...worldPoint } : prev;
                    });
                } else {
                    // The anchor is stored shape-relative (offset from movingShape.x/y),
                    // not as an absolute world point, so it doesn't drift when the shape itself moves.
                    const relativePoint = movingShape ? { x: worldPoint.x - movingShape.x, y: worldPoint.y - movingShape.y } : worldPoint;
                    umlConnectorEngine.setConnectorAnchor(connector.id, variant, relativePoint);
                }
            }

            function cleanup() {
                document.body.style.cursor = '';
                document.body.classList.remove('uml-reattach-dragging');
                window.removeEventListener('mousemove', onMouseMove, true);
                window.removeEventListener('mouseup', onMouseUp, true);
                window.removeEventListener('keydown', onKeyDown, true);
            }

            function onMouseUp(ev) {
                ev.stopImmediatePropagation();
                const upWorld = toWorldPoint(ev.clientX, ev.clientY);
                if (DEBUG_LOGGING_ENABLED) {
                    outputMsgEngine.addMsg(
                        'Debug',
                        'log',
                        `${connector.id.slice(0, 8)} | Anchor (${variant}) | mouseup | x:${Math.round(upWorld.x)} y:${Math.round(upWorld.y)}`,
                    );
                }
                if (isReattachMode) {
                    const worldPoint = upWorld;
                    // Excludes the connector's OTHER (fixed) end — dropping on it should be a
                    // no-op, not a self-loop (hard-blocked anyway by UmlConnectorEngine.reattachConnectorEnd).
                    const targetShape = ShapeHitTester.findAtPoint(umlCanvasEngine.shapes, worldPoint.x, worldPoint.y, fixedShapeId);
                    logDebug(
                        'handleAnchorDragStart.onMouseUp',
                        `world=${JSON.stringify(worldPoint)} targetShape=${targetShape?.id ?? 'none'}`,
                    );
                    if (targetShape) {
                        umlConnectorEngine.reattachConnectorEnd(connector.id, variant, targetShape.id);
                    }
                    setReattachDraft(null);
                }
                cleanup();
            }

            function onKeyDown(ev) {
                if (ev.key === 'Escape') {
                    setReattachDraft(null);
                    cleanup();
                }
            }

            window.addEventListener('mousemove', onMouseMove, true);
            window.addEventListener('mouseup', onMouseUp, true);
            window.addEventListener('keydown', onKeyDown, true);
        },
        [toWorldPoint, getShapeById],
    );

    const reattachFixedShape = reattachDraft ? getShapeById(reattachDraft.fixedShapeId) : null;
    const reattachStart = reattachFixedShape ? AnchorCalculator.edgeToward(reattachFixedShape, reattachDraft.x, reattachDraft.y) : null;

    return { reattachDraft, reattachStart, handleAnchorDragStart };
}
